import { useEffect, useState } from 'react';
import useConstant from 'use-constant';
import { cloneDeep } from 'lodash';
import { useAsyncCallback } from 'react-async-hook';
import DebouncePromise from 'awesome-debounce-promise';
import {
  FinanceResponse,
  FinanceResponseData,
  FinanceResponseId,
} from '@upstart/harmony/lib/legos/financingTypes';
import PaymentsService from '../../services/PaymentsService';
import FinanceResponseService from '../../services/FinanceResponseService';

const DEBOUNCE_WAIT = 1000; // milliseconds

const getPaymentsForFinanceResponseDebounced = DebouncePromise(
  PaymentsService.getPaymentsForFinanceResponse,
  DEBOUNCE_WAIT,
);

interface Props {
  responseId: FinanceResponseId;
}

const useFinanceResponsePayments = (props: Props) => {
  const [response, setResponse] = useState<FinanceResponse>();
  const [originalResponse, setOriginalResponse] = useState<FinanceResponse>();
  const getPayments = useConstant(() => getPaymentsForFinanceResponseDebounced);
  const asyncPayments = useAsyncCallback<FinanceResponse>(getPayments);
  const asyncResponse = useAsyncCallback<FinanceResponse>(
    FinanceResponseService.get,
  );

  function fetchPaymentsForResponse(financeResponse: FinanceResponse) {
    const hasBackendCalculationVersion = !!financeResponse.data
      ?.backendCalculationVersion;
    return asyncPayments.execute(financeResponse, {
      includeBackendAmount: hasBackendCalculationVersion,
      withRICDefaults: hasBackendCalculationVersion,
      includeValidations: true,
    });
  }

  useEffect(() => {
    if (props?.responseId) asyncResponse.execute(props.responseId);
    // eslint-disable-next-line
  }, [props.responseId]);

  useEffect(() => {
    if (asyncResponse.result) {
      setResponse(asyncResponse.result);
      setOriginalResponse(asyncResponse.result);
      // Once the intial response is loaded, fetch the payments and validations
      fetchPaymentsForResponse(asyncResponse.result).then(
        responseFromPayments => {
          setResponse(responseFromPayments);
          setOriginalResponse(responseFromPayments);
        },
      );
    }
  }, [asyncResponse.result]);

  const fetchResponseWithUpdatedData = (data: Partial<FinanceResponseData>) => {
    if (response) {
      const currentResponse = cloneDeep(response);
      const updatedResponse = {
        ...currentResponse,
        data: { ...currentResponse.data, ...data },
      };
      setResponse(updatedResponse);
      fetchPaymentsForResponse(updatedResponse)
        .then(responseFromPayments => {
          setResponse(responseFromPayments);
        })
        .catch(() => {
          // Reset the response on failure
          setResponse(currentResponse);
        });
    }
  };
  return {
    response,
    setResponse,
    resetOriginalResponse: () => asyncResponse.execute(props.responseId),
    originalResponse,
    loadingResponse: asyncResponse.loading,
    loadingPayments: asyncPayments.loading,
    responseError: asyncResponse.error,
    paymentsError: asyncPayments.error,
    fetchResponseWithUpdatedData,
  };
};

export default useFinanceResponsePayments;
